import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Leaflet from 'leaflet';

/**
 * Component that display a marker on the map.
 */
class Marker extends Component {
   componentDidMount() {
      this.marker = this.createMarker();
   }

   componentWillUnmount() {
      this.removeMarker();
   }

   componentDidUpdate(prevProps) {
                            // Delete the existing marker when the latitude or the longitude is not defined
      if (!this.props.latitude || !this.props.longitude) {
         this.removeMarker();
         return;
      }
                             // Create a new marker if it was not initialized in the componentDidMount lifecycle
      this.marker = this.marker ? this.marker : this.createMarker();

      if (this.props.popupText !== prevProps.popupText) {
         if (this.props.popupText && !prevProps.popupText)
            this.marker.bindPopup(this.props.popupText, { className: "marker-popup", closeButton: false }).openPopup();
         else if (this.props.popupText)
            this.marker.setPopupContent(this.props.popupText).openPopup();
         else if (!this.props.popupText)
            this.removePopup();
      }

                             // Update the marker icon
      if (this.props.iconUrl !== prevProps.iconUrl) {
         this.marker.setIcon(Leaflet.icon(this.getIconOptions()));
      }

                              // Update the marker position
      if (this.props.latitude !== prevProps.latitude || this.props.longitude !== prevProps.longitude) {
         var newPos = Leaflet.latLng(this.props.latitude, this.props.longitude);
         this.marker.setLatLng(newPos);

                             // Offset marker
         if (this.props.centerOffset) {
            var targetPoint = this.props.map.project(newPos).subtract([0, this.props.centerOffset() / 2]);
            newPos = this.props.map.unproject(targetPoint);
         }

         this.props.map.flyTo(newPos);
      }
   }
   /**
    * Create the marker and add it to the map
    * @returns {object} the marker object
    */
   createMarker() {
      if (!this.props.map || !this.props.latitude || !this.props.longitude) {
         return;
      }

      var marker = Leaflet.marker([this.props.latitude, this.props.longitude], {
         icon: Leaflet.icon(this.getIconOptions()),
         draggable: this.props.draggable
      });

      marker.addTo(this.props.map);

      if (this.props.draggable) {
         marker.on('dragend', () => {
            var position = marker.getLatLng();
            if (this.props.onDragEnd)
               this.props.onDragEnd(position);
         });
      }

      if (this.props.popupText) {
         /* todo: pour être générique, probablement que la classe et les options doivent être configurable */
         marker.bindPopup(this.props.popupText, {
            className: "marker-popup",
            closeButton: false
         }).openPopup().on('popupclose', () => {
            if (this.props.onClosePopup)
               this.props.onClosePopup();
         });
      }

      return (marker);
   }

   /**
    * Remove the popup on the marker
    */
   removePopup() {
      if (this.marker) {
         // remove popup
         this.marker.closePopup();
         this.marker.unbindPopup();
      }
   }

   /**
    * Remove the marker on the map
    */
   removeMarker() {
      if (this.marker) {
                             // remove popup
         this.removePopup();
                             // remove marker
         this.marker.remove();
         this.marker = null;
      }
   }
   /**
    * Get the icon options from the props
    * @returns {object} iconOptions object use to create the marker
    */
   getIconOptions() {
      const iconClassNames = classNames(this.props.className,
         {
            'leaflet-default-icon-path': !this.props.iconUrl
         });

      let iconOptions = {
         iconUrl: this.props.iconUrl,
         className: iconClassNames,
         iconAnchor: this.props.iconAnchor,
         popupAnchor: [0, this.props.iconAnchor? this.props.iconAnchor[1]*-1 : 0]
      };

      return (iconOptions);
   }

   /**
    * renders the marker component
    */
   render() {
      return (null);
   }
}

Marker.propTypes = {
   /**
    * defines the map object on which to add the marker
    */
   map: PropTypes.object,
   /**
    * defines the url for the marker icon
    */
   iconUrl: PropTypes.string,
   /**
    * Used to centered the icon by providing its coordinates. Useful when using a custom icon.
    * An array of two numnbers. The first number defines the icon css margin-left properties.
    * The second number defines the icon css margin-top properties.
    */
   iconAnchor: PropTypes.arrayOf(PropTypes.number),
   /**
    * defines the latitude of the marker
    */
   latitude: PropTypes.number,
   /**
    * defines the longitude of the marker
    */
   longitude: PropTypes.number,
   /**
    * defines the marker css classnames
    */
   className: PropTypes.string,
   /**
    * Indicates if the marker is draggable or not
    */
   draggable: PropTypes.bool,
   /**
    * A callback to execute when the user stops dragging the marker on the map.
    */
   onDragEnd: PropTypes.func,
   /**
    * Text to show in a popup over the marker, when empty, no popup is shown
    */
   popupText: PropTypes.string,
   /**
    * Callback to execute when marker popup is closed.
    */
   onClosePopup: PropTypes.func
}

export default Marker;
